import { Text } from '@/components/primitives';
import { emptyStates } from '@/constants';
import { useConnectivity } from '@/connectivity/useConnectivity';
import { UnreachableState } from '@/components/ui/UnreachableState';
import { region } from './regionLoadState.css';
import { RegionTryAgain } from './RegionTryAgain';

export { RegionTryAgain };

export interface RegionLoadStateProps {
  /** `failed`: the region's request errored. `offline`: the browser is offline.
   *  A union so the waiting state can join later (Q-509 holds it). */
  state: 'failed' | 'offline';
  /** The Try again action. Required for `failed`; `offline` never shows it. */
  onRetry?: () => void;
}

/**
 * The one failed / offline state for a region that loads data (Q-506 card
 * pattern-2 (Q-509 a) and card 1546). It owns the words: no other component
 * renders them.
 *
 * One `role="status"` live region. "Try again" is a real button in tab order;
 * focus is never moved to it.
 *
 * #1561-g: a `failed` region whose request ended without a response while the
 * connectivity controller says `unreachable` shows the inline UnreachableState
 * instead, exactly as a waiting region does (LoadingState: `unreachable &&
 * unreachableKind === 'unreachable'`). Its Retry clears the flag and then runs
 * `onRetry`, which re-sends this region's own request. When the controller
 * clears, the region shows the failed frame again if its query is still in error.
 */
export function RegionLoadState({ state, onRetry }: RegionLoadStateProps) {
  const { unreachable, unreachableKind } = useConnectivity();
  if (state === 'failed' && unreachable && unreachableKind === 'unreachable') {
    return <UnreachableState onRetry={onRetry} />;
  }
  return (
    <div role="status" className={region}>
      <Text color="secondary">
        {state === 'failed' ? emptyStates.error.regionFailed : emptyStates.error.regionOffline}
      </Text>
      {state === 'failed' && onRetry && <RegionTryAgain onRetry={onRetry} />}
    </div>
  );
}
